import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, PlusCircle } from 'lucide-react';
import { useVenue } from '../contexts/VenueContext';
import { useVenueSwitcher } from '../hooks/useVenueSwitcher';
import { getVenueRoleBadge } from '../utils/venueRoleBadge';
import NotificationBellButton from './NotificationBellButton';
import SafeAvatarImage from './SafeAvatarImage';
import { VenueGuestMessagesOffPill } from './venue/VenueGuestMessagesOffPill';
import ThemeToggle from './ThemeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const font = "'Inria Serif', serif";

export default function VenueHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeVenue, activeVenueAppId } = useVenue();
  const { venues, selectVenue, switching } = useVenueSwitcher();
  const [showDropdown, setShowDropdown] = useState(false);

  const getPhotoUrl = (v) => {
    const url = v?.photo_urls?.[0] || v?.main_photo || v?.photo_url || v?.image_url;
    if (!url) return null;
    return url.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
  };

  const getVenueName = (v) => (v?.map_name || v?.restaurant_name || v?.name || 'Venue').trim();
  const getVenueInitials = (v) => {
    const name = getVenueName(v);
    const parts = name.split(/\s+/).filter(Boolean);
    return (parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : name.substring(0, 2)).toUpperCase();
  };
  const renderVenueFallback = (v, testId) => (
    <div
      data-testid={testId}
      style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #F2DDB7 0%, #E3C28A 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <span style={{ fontFamily: font, fontSize: 14, fontWeight: 800, color: 'var(--status-warning-text)', letterSpacing: 0 }}>
        {getVenueInitials(v)}
      </span>
    </div>
  );
  const activeVenueName = getVenueName(activeVenue);

  return (
    <div style={{ width: '100%', maxWidth: 390, display: 'flex', flexDirection: 'column' }}>
    <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 0', position: 'relative' }}>
      {/* Venue photo + name + dropdown toggle */}
      <div style={{ position: 'relative', zIndex: 60, minWidth: 0, flex: 1, marginRight: 12 }}>
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 0, maxWidth: '100%' }}
          data-testid="venue-selector-btn"
        >
          <div style={{
            width: 44, height: 44, borderRadius: '50%', overflow: 'hidden',
            backgroundColor: 'var(--surface-2)', border: '2px solid var(--surface)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)', flexShrink: 0,
          }}>
            <SafeAvatarImage
              src={getPhotoUrl(activeVenue)}
              alt={activeVenueName || 'Venue'}
              dataTestId="venue-header-active-photo-image"
              fallback={renderVenueFallback(activeVenue, 'venue-header-active-photo-fallback')}
            />
          </div>
          {activeVenueName && (
            <span
              data-testid="venue-header-active-name"
              style={{
                fontFamily: font,
                fontSize: 18,
                fontWeight: 700,
                color: 'var(--text)',
                lineHeight: 1.15,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
                maxWidth: 180,
              }}
            >
              {activeVenueName}
            </span>
          )}
          <ChevronDown size={18} color="var(--text)" style={{ flexShrink: 0 }} />
        </button>

        {/* Venue selector dropdown */}
        {showDropdown && (
          <div data-testid="venue-selector-dropdown" style={{
            position: 'absolute', top: '100%', left: 0, marginTop: 6,
            backgroundColor: 'var(--surface)', borderRadius: 14, padding: '8px 6px',
            boxShadow: 'var(--shadow-2)',
            minWidth: 220, maxHeight: 280, overflowY: 'auto',
            zIndex: 200,
          }}>
            {venues.map(v => (
              <button key={v.id} onClick={() => { selectVenue(v); setShowDropdown(false); }}
                disabled={switching}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', width: '100%',
                  background: activeVenue?.id === v.id ? 'var(--accent-surface-soft)' : 'none',
                  border: 'none', cursor: switching ? 'not-allowed' : 'pointer', borderRadius: 10,
                  opacity: switching ? 0.6 : 1,
                }}>
                <div style={{ width: 44, height: 44, borderRadius: '50%', overflow: 'hidden', backgroundColor: 'var(--surface-2)', flexShrink: 0, border: '2px solid var(--surface)' }}>
                  <SafeAvatarImage
                    src={getPhotoUrl(v)}
                    alt={getVenueName(v)}
                    dataTestId={`venue-selector-photo-image-${v.id}`}
                    fallback={renderVenueFallback(v, `venue-selector-photo-fallback-${v.id}`)}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, minWidth: 0 }}>
                  <span style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }}>
                    {v.restaurant_name || 'Venue'}
                  </span>
                  <span
                    data-testid={`venue-selector-role-badge-${v.id}`}
                    style={{ fontFamily: font, fontSize: 11, fontWeight: 700, color: 'var(--accent-purple)', background: 'var(--accent-surface-soft)', padding: '1px 8px', borderRadius: 999 }}
                  >
                    {getVenueRoleBadge(v)}
                  </span>
                </div>
              </button>
            ))}
            {venues.length === 0 && (
              <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-3)', textAlign: 'center', padding: 10 }}>No venues yet</p>
            )}
            <button onClick={() => {
              const existing = JSON.parse(sessionStorage.getItem('venueOnboarding') || '{}');
              sessionStorage.setItem('venueOnboarding', JSON.stringify({
                ...existing,
                isAddingVenue: true,
                returnPath: `${location.pathname}${location.search}`,
                returnVenueAppId: activeVenueAppId || null,
              }));
              setShowDropdown(false);
              navigate('/venue/intro');
            }}
              data-testid="venue-add-btn"
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', padding: '9px 10px', marginTop: 8,
                fontFamily: font, fontSize: 15, fontWeight: 700, color: 'var(--accent-purple-strong)',
                background: 'var(--accent-surface-soft)', border: 'none', borderRadius: 12,
                cursor: 'pointer', transition: 'background-color 0.15s ease, transform 0.1s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent-surface)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent-surface-soft)'; }}
            >
              <span style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <PlusCircle size={18} color="var(--accent-purple-strong)" />
              </span>
              Add another venue
            </button>
          </div>
        )}
      </div>

      {/* Bell + ThemeToggle (on profile page only) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {location.pathname === '/venue/profile' && <ThemeToggle />}
        <NotificationBellButton buttonTestId="venue-header-bell-button" popupTestId="venue-header-notification-popup" />
      </div>
    </div>
      <VenueGuestMessagesOffPill style={{ alignSelf: 'flex-start', margin: '10px 20px 0' }} />
    </div>
  );
}
